import { Col, Row } from 'antd';
import type {
  CatalogPriceZoneSPPriceFamily,
  SelectedProductsAndSellingPrices,
} from 'hooks/catalog-price-zone-service-provider';
import type { OrderPrestataireLine } from '.';
import { getSellingPrice } from 'utils/PriceUtils';
import GenerateMargeSpacingSection from './GenerateMargeSpacingSection';

/**
 * Props for the ClientOrderPriceCell component.
 * @interface ClientOrderPriceCellProps
 */
interface ClientOrderPriceCellProps {
  /** Array of product details for displaying selling prices. */
  value?: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    selectedPriceFamilyId?: number | null;
    sellingUnitPrice?: number;
    priceValue?: number | null;
    documentProductLineId?: number;
  }[];
  /** Service provider order line data (not used directly in rendering). */
  record: OrderPrestataireLine | null;
  /** Array of client-selected products with their selling prices. */
  selectedProductsFromOrderClient: SelectedProductsAndSellingPrices[];
}

/**
 * A React component that displays the selling prices for products in a client order.
 * @param props - The component props.
 * @returns A JSX element rendering a section with selling prices and spacing for price families.
 * 
 * This component iterates over the provided product array (value) and displays the selling price
 * for each product, calculated using the getSellingPrice utility. It handles special cases for
 * "Transport Benne" products by prioritizing the price of "Transport Benne : Pose et enlèvement"
 * products. If price families exist, it includes a spacing section using GenerateMargeSpacingSection
 * to align the UI. Prices are formatted with European notation (comma as decimal separator) and
 * displayed with a euro symbol.
 */
const ClientOrderPriceCell = (props: ClientOrderPriceCellProps) => {
  const { value, selectedProductsFromOrderClient } = props;

  return (
    <section className="section">
      {value?.map((item) => {
        const hasTarifSection = item?.priceFamilies && item.priceFamilies.length > 0;
        const sellingPrice = getSellingPrice(item, selectedProductsFromOrderClient);

        return (
          <div key={`${item?.uuid}-${item.productId}`}>
            <Row gutter={[16, 0]} align="middle" className="generate-price-value-cell client-order-cell">
              <Col className="marge-section">
                <div className="prix-de-vente-cell">
                  {sellingPrice.toFixed(2).replace(/\./g, ',')} €
                </div>
              </Col>
            </Row>
            {hasTarifSection && (
              <div className="generate-price-value-cell">
                <GenerateMargeSpacingSection
                  priceFamilies={item?.priceFamilies || []}
                  priceFamilyId={item?.selectedPriceFamilyId}
                />
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default ClientOrderPriceCell;